import React from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import JSZip from 'jszip';
import { InteractiveReport } from '../reportView';
import { apiService } from '../services/api';
import type { MicroReport, ReportSectionsResponse } from '../services/api';

export interface WorkspaceExportProgress {
  current: number;
  total: number;
  title: string;
}

interface WorkspaceExportOptions {
  reportSections?: ReportSectionsResponse | null;
  hideMicroreportLinks?: boolean;
  workspaceName?: string;
  onProgress?: (progress: WorkspaceExportProgress) => void;
}

// Package order: Scope first, Quick Trends second, then microreports/custom, final last.
const EXPORT_ORDER: Record<string, number> = {
  scope: 0,
  early_insights: 1,
  step: 2,
  custom: 3,
  final: 4,
};

const sanitizeFilename = (name: string): string => {
  const cleaned = (name || '')
    .replace(/[\\/:*?"<>|]+/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);
  return cleaned || 'analysis';
};

// Framing has no document body (interactive cards); plan/brief are internal
// documents intentionally hidden from the workspace UI.
const EXCLUDED_TYPES = new Set(['framing', 'plan', 'brief']);
// Only one of each of these should ever be exported (latest version wins).
const SINGLETON_TYPES = new Set(['scope', 'early_insights', 'final']);

export const getExportableReports = (reports: MicroReport[]): MicroReport[] => {
  const seenIds = new Set<string>();
  const candidates = reports.filter((report) => {
    const type = (report.type || '').toLowerCase();
    if (EXCLUDED_TYPES.has(type)) return false;
    if (!(report.content || '').trim()) return false;
    if (seenIds.has(report.id)) return false;
    seenIds.add(report.id);
    return true;
  });

  // The reports array keeps regeneration history (old versions stay in the
  // list). Mirror MicroReportsPanel: keep only the latest version per
  // singleton type, and per title for step reports. Custom reports are
  // distinct documents, so they are always kept.
  const latest = new Map<string, MicroReport>();
  for (const report of candidates) {
    const type = (report.type || '').toLowerCase();
    const key = SINGLETON_TYPES.has(type)
      ? type
      : type === 'step'
        ? `step:${(report.title || '').trim().toLowerCase()}`
        : `${type}:${report.id}`;
    const existing = latest.get(key);
    if (!existing || new Date(report.date).getTime() >= new Date(existing.date).getTime()) {
      latest.set(key, report);
    }
  }

  return Array.from(latest.values()).sort((a, b) => {
    const orderA = EXPORT_ORDER[(a.type || '').toLowerCase()] ?? 2;
    const orderB = EXPORT_ORDER[(b.type || '').toLowerCase()] ?? 2;
    return orderA - orderB;
  });
};

// Renders a report off-screen with the same component the viewer uses,
// so the PDF endpoint receives identical HTML to a single-report export.
const renderReportHtml = async (
  report: MicroReport,
  reportSections: ReportSectionsResponse | null,
  hideMicroreportLinks?: boolean,
): Promise<string> => {
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-10000px';
  container.style.top = '0';
  container.style.width = '900px';
  container.style.pointerEvents = 'none';
  document.body.appendChild(container);
  const root = createRoot(container);
  try {
    flushSync(() => {
      root.render(
        <InteractiveReport
          markdown={report.content || ''}
          reportSections={reportSections}
          isFinalReport={(report.type || '').toLowerCase() === 'final'}
          hideMicroreportLinks={hideMicroreportLinks}
        />,
      );
    });
    await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));
    const node = container.querySelector('.interactive-report');
    return node ? node.outerHTML : '';
  } finally {
    root.unmount();
    container.remove();
  }
};

export const exportWorkspacePdfZip = async (
  reports: MicroReport[],
  options: WorkspaceExportOptions = {},
): Promise<void> => {
  const exportable = getExportableReports(reports);
  if (!exportable.length) {
    throw new Error('No completed analyses to export');
  }

  const zip = new JSZip();
  const usedNames = new Set<string>();

  for (let i = 0; i < exportable.length; i += 1) {
    const report = exportable[i];
    options.onProgress?.({ current: i + 1, total: exportable.length, title: report.title || '' });

    const html = await renderReportHtml(
      report,
      options.reportSections ?? null,
      options.hideMicroreportLinks,
    );
    if (!html.trim()) continue;

    const blob = await apiService.exportReportPdf(report.id, html, {
      title: report.title || 'analysis',
      is_custom: (report.type || '').toLowerCase() === 'custom',
    });

    let filename = `${String(i + 1).padStart(2, '0')} - ${sanitizeFilename(report.title || report.type)}`;
    let suffix = 2;
    while (usedNames.has(filename)) {
      filename = `${String(i + 1).padStart(2, '0')} - ${sanitizeFilename(report.title || report.type)} (${suffix})`;
      suffix += 1;
    }
    usedNames.add(filename);
    zip.file(`${filename}.pdf`, blob);
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(zipBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${sanitizeFilename(options.workspaceName || 'workspace')} - full package.zip`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
